// PAE con tu pauta: el PAE validado, re-expresado en el formato que exige la
// universidad del estudiante. Dominio PURO (sin React, sin Supabase, sin IA).
//
// Por qué existe (2026-09-15, plan `ops/planes/pae-con-tu-pauta.md`): 8 de
// cada 10 preguntas a Benito son tarea de PAE con la pauta de SU universidad
// (Gordon con expresión y satisfacción, Roper por actividades vitales,
// Astorquiza por necesidades, objetivo con fórmula, 3 actividades con fecha,
// hora y responsable, evaluación logrado / no logrado). Lo que piden no es otro
// contenido clínico: es el MISMO contenido ordenado de otra forma.
//
// Diseño en dos escalones, y este archivo es el primero:
//  1. DETERMINISTA (aquí): cada diagnóstico validado se asigna a una sección
//     de la pauta por un mapa cerrado NANDA → sección; el objetivo NOC se vierte
//     en la fórmula; las actividades NIC se recortan a 3 por prioridad y se les
//     agrega fecha, hora y responsable; la evaluación queda EN BLANCO, porque
//     un plan no es un hecho (regla del fundador) y la app jamás marca
//     "logrado" sola.
//  2. IA (`paePautaIA.js`): solo escribe la prosa que la pauta pide y el PAE no
//     trae (expresión, satisfacción, condicionantes, mecanismo de producción).
//     `verificarPauta` es la red determinista que la vigila: no puede agregar
//     diagnósticos, ni cifras, ni PII, ni tocar campos que no le tocan.
//
// El motor clínico del PAE (diagnosisPrompt, prompts del Detallado, la suite
// R1→R6 y el pin de Sonnet 4.6) NO se toca: esta capa va DESPUÉS del PAE
// validado y no re-diagnostica. Fijado por test en aiConfig.
//
// La entrada es el PAE NORMALIZADO que ya construye `pdfPayload.js`
// (`buildRapidoPayload` / `buildDetalladoPayload`): `diagnosticos[]` con
// `codigo`, `titulo`, `noc[{code,label,indicadores[{texto,plazo,evaluar}]}]`
// y `nic[{code,label,actividades[{texto,dimension,fundamento}]}]`. Un solo
// código sirve para el Rápido y el Detallado.

import { NANDA_DB, HENDERSON_NEEDS } from '../data/nanda';
import { detectPiiDuro } from './piiDetect';

// ─── Catálogo de pautas ──────────────────────────────────────────────────────
//
// Cada pauta: secciones (catálogo cerrado, en el orden en que se imprimen),
// los campos de prosa que la pauta pide POR SECCIÓN (los escribe la IA en el
// escalón 2, o quedan "por completar"), la fórmula del objetivo y las opciones
// de evaluación. Las taxonomías vienen de la fuente que se cita en cada una.

// Gordon, M. Manual de diagnósticos enfermeros (11 patrones funcionales).
const SECCIONES_GORDON = [
  { id: 'g01', nombre: 'Percepción y manejo de la salud' },
  { id: 'g02', nombre: 'Nutricional y metabólico' },
  { id: 'g03', nombre: 'Eliminación' },
  { id: 'g04', nombre: 'Actividad y ejercicio' },
  { id: 'g05', nombre: 'Sueño y descanso' },
  { id: 'g06', nombre: 'Cognitivo y perceptual' },
  { id: 'g07', nombre: 'Autopercepción y autoconcepto' },
  { id: 'g08', nombre: 'Rol y relaciones' },
  { id: 'g09', nombre: 'Sexualidad y reproducción' },
  { id: 'g10', nombre: 'Adaptación y tolerancia al estrés' },
  { id: 'g11', nombre: 'Valores y creencias' },
];

// Roper, Logan y Tierney. The Elements of Nursing (12 actividades vitales).
const SECCIONES_ROPER = [
  { id: 'r01', nombre: 'Mantener un entorno seguro' },
  { id: 'r02', nombre: 'Comunicación' },
  { id: 'r03', nombre: 'Respiración' },
  { id: 'r04', nombre: 'Comer y beber' },
  { id: 'r05', nombre: 'Eliminación' },
  { id: 'r06', nombre: 'Higiene personal y vestido' },
  { id: 'r07', nombre: 'Control de la temperatura corporal' },
  { id: 'r08', nombre: 'Movilización' },
  { id: 'r09', nombre: 'Trabajo y ocio' },
  { id: 'r10', nombre: 'Expresión de la sexualidad' },
  { id: 'r11', nombre: 'Sueño' },
  { id: 'r12', nombre: 'Muerte' },
];

// Modelo de necesidades humanas de Inés Astorquiza. Agrupar en tres secciones
// (biológica, psicológica y social) es un FORMATO de MiPAE: en las fuentes
// leídas cada área tiene varios estados de necesidad (Barrientos Burgos y
// Sánchez Montecinos, 2024). El nombre exacto de cada necesidad lo pone la
// pauta de cada universidad (varía entre casas de estudio), por eso la sección
// es el área y la necesidad se imprime como el problema del diagnóstico.
// Revisión bibliográfica del 3-oct-2026: la fuente decía "texto de la autora"
// sin que nadie lo hubiera leído; se corrigió (registro en
// docs/revision-bibliografica-modelos-2026-10-03.md).
const SECCIONES_ASTORQUIZA = [
  { id: 'a_bio', nombre: 'Estado biológico' },
  { id: 'a_psi', nombre: 'Estado psicológico' },
  { id: 'a_soc', nombre: 'Estado social' },
];

// Henderson: reusa el catálogo que ya usa el PAE Rápido (fuente única).
const SECCIONES_HENDERSON = HENDERSON_NEEDS.map((n) => ({ id: `h_${n.id}`, nombre: n.label }));

export const SECCION_OTROS = { id: 'otros', nombre: 'Otros hallazgos' };

export const EVALUACION_OPCIONES = ['Logrado', 'Parcialmente logrado', 'No logrado'];

export const RESPONSABLES = ['Estudiante de enfermería', 'Enfermero/a a cargo', 'Estudiante con supervisión'];

export const PAUTAS = {
  gordon: {
    id: 'gordon',
    nombre: 'Patrones funcionales de Gordon',
    descripcion: 'Once patrones, cada uno con expresión, satisfacción y factores condicionantes; jerarquización con justificación.',
    fuente: 'Gordon, M. Manual de diagnósticos enfermeros. NANDA-I 2024-2026 para la correspondencia dominio a patrón.',
    secciones: SECCIONES_GORDON,
    camposSeccion: ['expresion', 'satisfaccion', 'condicionantes'],
    jerarquiza: true,
  },
  roper: {
    id: 'roper',
    nombre: 'Roper, Logan y Tierney',
    descripcion: 'Doce actividades vitales ordenadas por magnitud del daño.',
    fuente: 'Roper, N., Logan, W., Tierney, A. The Elements of Nursing.',
    secciones: SECCIONES_ROPER,
    camposSeccion: ['magnitud'],
    jerarquiza: true,
  },
  astorquiza: {
    id: 'astorquiza',
    nombre: 'Necesidades según Astorquiza',
    descripcion: 'Estados biológico, psicológico y social; cada necesidad con mecanismo de expresión y de satisfacción, y en lo biológico además el de producción.',
    // Lo que se imprime en el PDF como "Pauta según". Dice qué es formato de
    // MiPAE y qué tiene respaldo leído; la obra original (1976) no se leyó.
    fuente: 'formato de MiPAE para el modelo de necesidades humanas de Inés Astorquiza. Estados de necesidad y mecanismos según Barrientos Burgos y Sánchez Montecinos (2024), Cuidarte 13(25). El nombre de cada necesidad sigue la pauta de tu universidad.',
    // Solo en pantalla (no viaja a la IA que redacta la pauta, así su entrada
    // no cambia). El formato pide expresión y satisfacción también en lo
    // psicológico y lo social, y las fuentes leídas no coinciden en eso: se
    // dice, sin convertir el formato en regla del modelo. Cambiar los campos
    // queda como propuesta en el registro de la revisión.
    nota: 'Este formato es una adaptación de MiPAE y pide los mecanismos de expresión y de satisfacción también en lo psicológico y lo social. Las fuentes no coinciden en eso. Barrientos y Sánchez (2024) no dividen esas áreas en mecanismos, y el capítulo de Triviño, Yusef y Seguel (2024) dice que todos los estados se rigen por los tres mecanismos. Tu docente puede indicarte qué forma usar en tu trabajo; la diferencia entre las fuentes sigue abierta.',
    secciones: SECCIONES_ASTORQUIZA,
    camposSeccion: ['expresion', 'satisfaccion'],
    camposExtraPorSeccion: { a_bio: ['produccion'] },
    jerarquiza: false,
  },
  henderson: {
    id: 'henderson',
    nombre: 'Necesidades de Henderson',
    descripcion: 'Las catorce necesidades básicas, la pauta clásica.',
    fuente: 'Henderson, V. The Nature of Nursing.',
    secciones: SECCIONES_HENDERSON,
    camposSeccion: [],
    jerarquiza: false,
  },
  libre: {
    id: 'libre',
    nombre: 'Mi pauta',
    descripcion: 'Pega los encabezados de la rúbrica de tu universidad y MiPAE reparte tu PAE en ellos.',
    fuente: null,
    secciones: [],
    camposSeccion: ['expresion', 'satisfaccion'],
    jerarquiza: true,
  },
};

export const PAUTA_IDS = Object.keys(PAUTAS);

// Etiquetas visibles de los campos de prosa (mismo vocabulario en preview,
// documento y prompt).
export const CAMPO_LABEL = {
  expresion: 'Mecanismo de expresión',
  satisfaccion: 'Mecanismo de satisfacción',
  condicionantes: 'Factores condicionantes',
  produccion: 'Mecanismo de producción',
  magnitud: 'Magnitud del daño',
};

// ─── Mapa NANDA → sección ────────────────────────────────────────────────────
//
// Regla por DOMINIO NANDA-I (el que trae `NANDA_DB`), con excepciones por
// CÓDIGO donde la taxonomía del modelo discrepa del dominio (por ejemplo, en
// Gordon lo respiratorio y lo cardiovascular van a "Actividad y ejercicio", no
// a "Eliminación e intercambio"; el sueño va a su propio patrón aunque NANDA lo
// ponga en "Actividad/Reposo"). Un diagnóstico que no calza en ninguna cae a
// SECCION_OTROS: fail-safe hacia mostrar, jamás hacia perderlo.

const DOMINIO_A_SECCION = {
  gordon: {
    'Promoción de la salud': 'g01',
    'Conducta de salud': 'g01',
    'Nutrición': 'g02',
    'Seguridad/Protección': 'g02',
    'Eliminación e Intercambio': 'g03',
    'Actividad/Reposo': 'g04',
    'Percepción/Cognición': 'g06',
    'Confort': 'g06',
    'Autopercepción': 'g07',
    'Relaciones de rol': 'g08',
    'Rol/Relaciones': 'g08',
    'Sexualidad': 'g09',
    'Afrontamiento/Tolerancia al estrés': 'g10',
    'Principios vitales': 'g11',
  },
  roper: {
    'Promoción de la salud': 'r01',
    'Conducta de salud': 'r01',
    'Nutrición': 'r04',
    'Seguridad/Protección': 'r01',
    'Eliminación e Intercambio': 'r05',
    'Actividad/Reposo': 'r08',
    'Percepción/Cognición': 'r02',
    'Confort': 'r02',
    'Autopercepción': 'r02',
    'Relaciones de rol': 'r09',
    'Rol/Relaciones': 'r09',
    'Sexualidad': 'r10',
    'Afrontamiento/Tolerancia al estrés': 'r02',
    'Principios vitales': 'r12',
  },
  astorquiza: {
    'Promoción de la salud': 'a_soc',
    'Conducta de salud': 'a_soc',
    'Nutrición': 'a_bio',
    'Seguridad/Protección': 'a_bio',
    'Eliminación e Intercambio': 'a_bio',
    'Actividad/Reposo': 'a_bio',
    'Percepción/Cognición': 'a_psi',
    'Confort': 'a_bio',
    'Autopercepción': 'a_psi',
    'Relaciones de rol': 'a_soc',
    'Rol/Relaciones': 'a_soc',
    'Sexualidad': 'a_soc',
    'Afrontamiento/Tolerancia al estrés': 'a_psi',
    'Principios vitales': 'a_psi',
  },
  henderson: {
    'Promoción de la salud': 'h_aprendizaje',
    'Conducta de salud': 'h_aprendizaje',
    'Nutrición': 'h_nutricion',
    'Seguridad/Protección': 'h_seguridad',
    'Eliminación e Intercambio': 'h_eliminacion',
    'Actividad/Reposo': 'h_movilidad',
    'Percepción/Cognición': 'h_comunicacion',
    'Confort': 'h_seguridad',
    'Autopercepción': 'h_comunicacion',
    'Relaciones de rol': 'h_trabajorol',
    'Rol/Relaciones': 'h_trabajorol',
    'Sexualidad': 'h_trabajorol',
    'Afrontamiento/Tolerancia al estrés': 'h_comunicacion',
    'Principios vitales': 'h_creencias',
  },
};

// Excepciones por código NANDA (misma sección en cada pauta cuando el
// dominio no representa lo que la pauta clasifica). Fuente por fila: la
// taxonomía de cada modelo.
const CODIGO_A_SECCION = {
  // Respiratorio y cardiovascular: Gordon los lleva a actividad y ejercicio,
  // Roper a respiración, Henderson a respiración o circulación.
  '00030': { gordon: 'g04', roper: 'r03', henderson: 'h_respiracion', astorquiza: 'a_bio' },
  '00031': { gordon: 'g04', roper: 'r03', henderson: 'h_respiracion', astorquiza: 'a_bio' },
  '00032': { gordon: 'g04', roper: 'r03', henderson: 'h_respiracion', astorquiza: 'a_bio' },
  '00033': { gordon: 'g04', roper: 'r03', henderson: 'h_respiracion', astorquiza: 'a_bio' },
  '00034': { gordon: 'g04', roper: 'r03', henderson: 'h_respiracion', astorquiza: 'a_bio' },
  '00029': { gordon: 'g04', roper: 'r03', henderson: 'h_circulacion', astorquiza: 'a_bio' },
  '00204': { gordon: 'g04', roper: 'r03', henderson: 'h_circulacion', astorquiza: 'a_bio' },
  '00201': { gordon: 'g04', roper: 'r03', henderson: 'h_circulacion', astorquiza: 'a_bio' },
  '00024': { gordon: 'g04', roper: 'r03', henderson: 'h_circulacion', astorquiza: 'a_bio' },
  // Sueño: patrón propio en Gordon, actividad vital propia en Roper.
  '00095': { gordon: 'g05', roper: 'r11', henderson: 'h_sueno', astorquiza: 'a_bio' },
  '00198': { gordon: 'g05', roper: 'r11', henderson: 'h_sueno', astorquiza: 'a_bio' },
  '00096': { gordon: 'g05', roper: 'r11', henderson: 'h_sueno', astorquiza: 'a_bio' },
  // Temperatura: nutricional y metabólico en Gordon, control de temperatura
  // en Roper, termorregulación en Henderson.
  '00007': { gordon: 'g02', roper: 'r07', henderson: 'h_temperatura', astorquiza: 'a_bio' },
  '00006': { gordon: 'g02', roper: 'r07', henderson: 'h_temperatura', astorquiza: 'a_bio' },
  '00008': { gordon: 'g02', roper: 'r07', henderson: 'h_temperatura', astorquiza: 'a_bio' },
  // Piel e infección: nutricional y metabólico en Gordon, higiene e
  // integridad en Henderson.
  '00046': { gordon: 'g02', roper: 'r06', henderson: 'h_higiene', astorquiza: 'a_bio' },
  '00047': { gordon: 'g02', roper: 'r06', henderson: 'h_higiene', astorquiza: 'a_bio' },
  '00044': { gordon: 'g02', roper: 'r06', henderson: 'h_higiene', astorquiza: 'a_bio' },
  '00004': { gordon: 'g02', roper: 'r01', henderson: 'h_seguridad', astorquiza: 'a_bio' },
  // Caídas y lesión: actividad y ejercicio en Gordon, entorno seguro en Roper.
  '00155': { gordon: 'g04', roper: 'r01', henderson: 'h_seguridad', astorquiza: 'a_bio' },
  '00303': { gordon: 'g04', roper: 'r01', henderson: 'h_seguridad', astorquiza: 'a_bio' },
  '00035': { gordon: 'g04', roper: 'r01', henderson: 'h_seguridad', astorquiza: 'a_bio' },
  // Dolor: cognitivo y perceptual en Gordon.
  '00132': { gordon: 'g06', roper: 'r02', henderson: 'h_seguridad', astorquiza: 'a_bio' },
  '00133': { gordon: 'g06', roper: 'r02', henderson: 'h_seguridad', astorquiza: 'a_bio' },
  // Movilidad y autocuidado.
  '00085': { gordon: 'g04', roper: 'r08', henderson: 'h_movilidad', astorquiza: 'a_bio' },
  '00091': { gordon: 'g04', roper: 'r08', henderson: 'h_movilidad', astorquiza: 'a_bio' },
  '00108': { gordon: 'g04', roper: 'r06', henderson: 'h_higiene', astorquiza: 'a_bio' },
  '00109': { gordon: 'g04', roper: 'r06', henderson: 'h_vestido', astorquiza: 'a_bio' },
  '00102': { gordon: 'g04', roper: 'r04', henderson: 'h_nutricion', astorquiza: 'a_bio' },
  // Volumen de líquidos: nutricional y metabólico en Gordon, comer y beber en
  // Roper.
  '00027': { gordon: 'g02', roper: 'r04', henderson: 'h_nutricion', astorquiza: 'a_bio' },
  '00028': { gordon: 'g02', roper: 'r04', henderson: 'h_nutricion', astorquiza: 'a_bio' },
  '00026': { gordon: 'g02', roper: 'r04', henderson: 'h_nutricion', astorquiza: 'a_bio' },
  '00025': { gordon: 'g02', roper: 'r04', henderson: 'h_nutricion', astorquiza: 'a_bio' },
  // Confusión, conciencia y comunicación.
  '00128': { gordon: 'g06', roper: 'r02', henderson: 'h_comunicacion', astorquiza: 'a_psi' },
  '00129': { gordon: 'g06', roper: 'r02', henderson: 'h_comunicacion', astorquiza: 'a_psi' },
  '00051': { gordon: 'g06', roper: 'r02', henderson: 'h_comunicacion', astorquiza: 'a_soc' },
  // Ansiedad y temor: adaptación y tolerancia al estrés.
  '00146': { gordon: 'g10', roper: 'r02', henderson: 'h_comunicacion', astorquiza: 'a_psi' },
  '00148': { gordon: 'g10', roper: 'r02', henderson: 'h_comunicacion', astorquiza: 'a_psi' },
};

// Pistas por PALABRA de la etiqueta, para diagnósticos que la IA propuso y no
// están en NANDA_DB (el modelo puede sugerir etiquetas fuera de las 92
// locales). Se evalúan en orden; la primera que calza manda. Límite de palabra
// obligatorio: "pie" cabe en "piel".
const PISTAS_ETIQUETA = [
  { re: /\b(respira|gaseoso|v[ií]as? a[eé]reas?|ventilaci[oó]n|aspiraci[oó]n)/i, dominio: 'Actividad/Reposo', codigoLike: '00032' },
  { re: /\b(gasto card[ií]aco|perfusi[oó]n|sangrado|hemorragia|card[ií]aco)/i, dominio: 'Actividad/Reposo', codigoLike: '00029' },
  { re: /\b(sue[nñ]o|insomnio|descanso)\b/i, dominio: 'Actividad/Reposo', codigoLike: '00095' },
  { re: /\b(hipertermia|hipotermia|termorregulaci[oó]n|temperatura)\b/i, dominio: 'Seguridad/Protección', codigoLike: '00007' },
  { re: /\b(piel|cut[aá]ne[ao]|tisular|presi[oó]n|herida|mucosa)\b/i, dominio: 'Seguridad/Protección', codigoLike: '00046' },
  { re: /\b(infecci[oó]n)\b/i, dominio: 'Seguridad/Protección', codigoLike: '00004' },
  { re: /\b(ca[ií]das?|lesi[oó]n|trauma)\b/i, dominio: 'Seguridad/Protección', codigoLike: '00155' },
  { re: /\b(dolor)\b/i, dominio: 'Confort', codigoLike: '00132' },
  { re: /\b(movilidad|deambulaci[oó]n|actividad|fatiga|intolerancia)\b/i, dominio: 'Actividad/Reposo', codigoLike: '00085' },
  { re: /\b(autocuidado|ba[nñ]o|vestido|higiene)\b/i, dominio: 'Actividad/Reposo', codigoLike: '00108' },
  { re: /\b(l[ií]quidos|volumen|hidrataci[oó]n|electrolit)/i, dominio: 'Nutrición', codigoLike: '00027' },
  { re: /\b(nutrici|ingesta|degluci[oó]n|glicemia|glucosa)/i, dominio: 'Nutrición', codigoLike: null },
  { re: /\b(eliminaci[oó]n|urinari|intestinal|estre[nñ]imiento|diarrea|incontinencia)/i, dominio: 'Eliminación e Intercambio', codigoLike: null },
  { re: /\b(confusi[oó]n|conciencia|cognitiv|memoria|percep)/i, dominio: 'Percepción/Cognición', codigoLike: '00128' },
  { re: /\b(comunicaci[oó]n verbal)\b/i, dominio: 'Percepción/Cognición', codigoLike: '00051' },
  { re: /\b(ansiedad|temor|miedo|afrontamiento|estr[eé]s|duelo|impotencia)\b/i, dominio: 'Afrontamiento/Tolerancia al estrés', codigoLike: '00146' },
  { re: /\b(autoestima|imagen corporal|identidad)\b/i, dominio: 'Autopercepción', codigoLike: null },
  { re: /\b(rol|familiar|cuidador|social|aislamiento)\b/i, dominio: 'Relaciones de rol', codigoLike: null },
  { re: /\b(sexual)/i, dominio: 'Sexualidad', codigoLike: null },
  { re: /\b(espiritual|creencias|religios|sufrimiento moral)\b/i, dominio: 'Principios vitales', codigoLike: null },
  { re: /\b(conocimientos?|salud|autogesti[oó]n|adherencia|r[eé]gimen)\b/i, dominio: 'Promoción de la salud', codigoLike: null },
];

const NANDA_POR_CODIGO = new Map(NANDA_DB.map((d) => [d.code, d]));

// Devuelve el id de sección de `pautaId` para un diagnóstico normalizado.
// Orden: excepción por código → dominio del catálogo local → pista por
// etiqueta (código "parecido" con excepción, si la hay, o dominio) → otros.
export function seccionParaDiagnostico(pautaId, dx) {
  const pauta = PAUTAS[pautaId];
  if (!pauta || pautaId === 'libre') return SECCION_OTROS.id;
  const codigo = String(dx?.codigo || '').trim();
  const porCodigo = CODIGO_A_SECCION[codigo]?.[pautaId];
  if (porCodigo) return porCodigo;
  const local = NANDA_POR_CODIGO.get(codigo);
  const mapa = DOMINIO_A_SECCION[pautaId] || {};
  if (local?.domain && mapa[local.domain]) return mapa[local.domain];
  const titulo = String(dx?.titulo || '');
  for (const pista of PISTAS_ETIQUETA) {
    if (!pista.re.test(titulo)) continue;
    const viaCodigo = pista.codigoLike ? CODIGO_A_SECCION[pista.codigoLike]?.[pautaId] : null;
    if (viaCodigo) return viaCodigo;
    if (mapa[pista.dominio]) return mapa[pista.dominio];
  }
  return SECCION_OTROS.id;
}

// ─── Pauta libre: los encabezados de la rúbrica del estudiante ───────────────
//
// Texto libre tras la guarda DURA de PII (la rúbrica no debería traer datos
// de nadie, pero un estudiante puede pegar la hoja entera). Máximo 12
// secciones, una por línea o separadas por punto y coma. Devuelve secciones
// con id estable (`l01`…) y el motivo si se rechazó.
export const MAX_SECCIONES_LIBRE = 12;

export function seccionesDePautaLibre(texto) {
  const crudo = String(texto || '');
  const lineas = crudo
    .split(/\r?\n|;/)
    .map((l) => l.replace(/^[\s\-•*\d.)]+/, '').replace(/[:\s]+$/, '').trim())
    .filter((l) => l.length >= 3 && l.length <= 80);
  // La guarda corre POR ENCABEZADO: dos títulos con mayúscula en líneas
  // seguidas ("Sueño / Respiración") le parecen un nombre completo a la guarda
  // si se le pasa el bloque entero.
  if (lineas.some((l) => detectPiiDuro(l).hit)) return { ok: false, motivo: 'pii', secciones: [] };
  const nombres = lineas;
  const unicos = [...new Set(nombres.map((n) => n.toLowerCase()))].map((k) => nombres.find((n) => n.toLowerCase() === k));
  if (!unicos.length) return { ok: false, motivo: 'vacia', secciones: [] };
  const secciones = unicos.slice(0, MAX_SECCIONES_LIBRE).map((nombre, i) => ({ id: `l${String(i + 1).padStart(2, '0')}`, nombre }));
  return { ok: true, motivo: null, secciones };
}

// En la pauta libre el reparto es por PISTAS sobre el nombre de la sección
// (la misma tabla de etiquetas, aplicada al encabezado del estudiante): la
// sección que "suena" a respiratorio recibe los diagnósticos respiratorios.
// Lo que no calza va a "Otros hallazgos" (se agrega al final si hace falta).
function seccionLibreParaDiagnostico(secciones, dx) {
  const dominioDx = dominioDe(dx);
  for (const sec of secciones) {
    const pista = PISTAS_ETIQUETA.find((p) => p.re.test(sec.nombre));
    if (pista && pista.dominio === dominioDx) return sec.id;
  }
  return SECCION_OTROS.id;
}

function dominioDe(dx) {
  const local = NANDA_POR_CODIGO.get(String(dx?.codigo || '').trim());
  if (local?.domain) return local.domain;
  const pista = PISTAS_ETIQUETA.find((p) => p.re.test(String(dx?.titulo || '')));
  return pista?.dominio || null;
}

// ─── Fórmula del objetivo ────────────────────────────────────────────────────
//
// "El paciente {verbo} {resultado NOC} al cabo de {plazo}, evidenciado por
// {indicadores}". El verbo sale del TIPO de diagnóstico: un "Riesgo de" se
// mantiene libre del problema; el resto mejora. El plazo sale de la
// temporalidad NOC que ya trae el PAE; si no hay, del plazo que eligió el
// estudiante; si tampoco, se deja explícito que lo define el equipo. Nada se
// inventa.
function verboObjetivo(titulo) {
  return /^riesgo de/i.test(String(titulo || '').trim()) ? 'se mantendrá libre de' : 'mejorará';
}

function minusculaInicial(s) {
  const t = String(s || '').trim();
  return t ? t[0].toLowerCase() + t.slice(1) : '';
}

function plazoDe(dx, plazoElegido) {
  const conPlazo = (dx?.noc || []).flatMap((n) => n.indicadores || []).find((i) => i?.plazo);
  if (conPlazo?.plazo) return String(conPlazo.plazo);
  if (plazoElegido) return String(plazoElegido);
  return 'el plazo que defina el equipo';
}

export function objetivoConFormula(dx, { plazoElegido } = {}) {
  const noc = (dx?.noc || [])[0];
  const verbo = verboObjetivo(dx?.titulo);
  const que = /^riesgo de/i.test(String(dx?.titulo || '').trim())
    ? minusculaInicial(String(dx.titulo).replace(/^riesgo de\s*/i, ''))
    : minusculaInicial(noc?.label || dx?.problema || dx?.titulo || 'su condición');
  const indicadores = (noc?.indicadores || []).map((i) => i?.texto).filter(Boolean);
  const evidencia = indicadores.length ? `, evidenciado por ${indicadores.join('; ')}` : '';
  return `El paciente ${verbo} ${que} al cabo de ${plazoDe(dx, plazoElegido)}${evidencia}.`;
}

// ─── Actividades: 3 por diagnóstico, con fecha, hora y responsable ───────────
//
// Las NIC ya vienen priorizadas del PAE (orden = prioridad). Se aplanan en ese
// orden y se toman las tres primeras. La hora es catálogo por turno; la fecha
// es la de entrega o la de hoy; el responsable, catálogo. Jamás un nombre de
// persona.
export const ACTIVIDADES_POR_DX = 3;
export const HORAS_POR_TURNO = { largo: ['08:00', '12:00', '16:00'], noche: ['20:00', '00:00', '04:00'], manana: ['08:00', '11:00', '14:00'] };

export function actividadesConPlan(dx, { fecha, turno = 'largo', responsable = RESPONSABLES[0] } = {}) {
  const horas = HORAS_POR_TURNO[turno] || HORAS_POR_TURNO.largo;
  const planas = (dx?.nic || []).flatMap((n) => (n.actividades || []).map((a) => ({
    texto: String(a?.texto || '').trim(),
    nicCode: n.code || '',
    nicLabel: n.label || '',
    dimension: a?.dimension || null,
  }))).filter((a) => a.texto);
  return planas.slice(0, ACTIVIDADES_POR_DX).map((a, i) => ({
    ...a,
    fecha: fecha || null,
    hora: horas[i % horas.length],
    responsable: RESPONSABLES.includes(responsable) ? responsable : RESPONSABLES[0],
  }));
}

function sinPlaceholder(v) {
  if (v == null) return '';
  const t = String(v).trim();
  return t === '—' || t === '-' || t === '–' ? '' : t;
}

// ─── Qué dice una sección sin diagnósticos ───────────────────────────────────
//
// Regla del fundador (auditoría del 4-oct-2026): "Sin alteración identificada"
// solo es un dato verdadero si esa categoría NO se marcó como alterada en la
// valoración. Si se marcó y el PAE no planificó un diagnóstico para ella (el
// Rápido planifica uno), se dice eso, sin afirmar normalidad de lo que no se
// planificó (un plan no es un hecho, y su ausencia tampoco es un hallazgo).
//
// `marcada` por sección: true (se marcó), false (no se marcó) o null (no se
// puede saber: se valoró con otro modelo o en formato libre, y las categorías
// de la pauta no son las de la valoración). Ante la duda, la frase que nunca
// es falsa. Sin `opciones.valoracion` (quien llama no la pasa) la sección no
// lleva la clave y el documento sale idéntico al de antes.
export const TEXTO_SIN_ALTERACION = 'Sin alteración identificada en este PAE.';
export const TEXTO_SIN_DIAGNOSTICO = 'Sin diagnóstico planificado en este PAE.';

export function textoSeccionSinDiagnostico(seccion) {
  return seccion?.marcada === true || seccion?.marcada === null ? TEXTO_SIN_DIAGNOSTICO : TEXTO_SIN_ALTERACION;
}

// `valoracion`: { modelo, marcadas: [ids de categoría] } (solo ids de
// catálogo, jamás hallazgos). Las secciones de Gordon, Roper y Astorquiza
// tienen los MISMOS ids que las categorías con que se valora (paeModelos),
// así que solo se cruzan cuando la pauta es la del modelo con que se valoró.
// Las de Henderson llevan prefijo (`h_respiracion` por la necesidad
// `respiracion`, SECCIONES_HENDERSON): sin quitarlo, una necesidad marcada
// nunca calzaba y su sección decía "Sin alteración identificada" (auditoría
// clínica del 4-oct-2026).
function marcadaEnValoracion(valoracion, pautaId, seccionId) {
  if (!valoracion) return undefined;
  if (valoracion.modelo !== pautaId) return null;
  const marcadas = new Set(Array.isArray(valoracion.marcadas) ? valoracion.marcadas : []);
  const categoria = pautaId === 'henderson' && seccionId.startsWith('h_') ? seccionId.slice(2) : seccionId;
  return marcadas.has(categoria);
}

// El enunciado del diagnóstico tal como pasó la revisión (R/C, M/P y
// razonamiento), para el documento determinista. NO entra al mensaje de la
// IA de la pauta (`paePautaIA` lee titulo, datosRelevantes, justificacion,
// objetivo y actividades; el enunciado vive aparte a propósito).
function enunciadoDe(dx) {
  const e = {
    relacionadoCon: String(dx?.relacionadoCon || '').trim(),
    manifestadoPor: String(dx?.manifestadoPor || '').trim(),
    razonamiento: String(dx?.razonamiento || '').trim(),
  };
  return e.relacionadoCon || e.manifestadoPor || e.razonamiento ? e : null;
}

// ─── El esqueleto ────────────────────────────────────────────────────────────
//
// `pae`: payload normalizado de pdfPayload. `opciones`: { fechaEntrega,
// plazoElegido, turno, responsable, seccionesLibre, valoracion }.
export function armarEsqueletoPauta(pae, pautaId, opciones = {}) {
  const pauta = PAUTAS[pautaId];
  if (!pauta) return null;
  const secciones = pautaId === 'libre' ? (opciones.seccionesLibre || []) : pauta.secciones;
  const diagnosticos = Array.isArray(pae?.diagnosticos) ? pae.diagnosticos : [];

  const porSeccion = new Map(secciones.map((s) => [s.id, []]));
  porSeccion.set(SECCION_OTROS.id, []);

  diagnosticos.forEach((dx, idx) => {
    const seccionId = pautaId === 'libre' ? seccionLibreParaDiagnostico(secciones, dx) : seccionParaDiagnostico(pautaId, dx);
    const destino = porSeccion.has(seccionId) ? seccionId : SECCION_OTROS.id;
    porSeccion.get(destino).push({
      orden: idx + 1,
      codigo: String(dx?.codigo || ''),
      titulo: String(dx?.titulo || ''),
      prioridad: dx?.prioridad || null,
      justificacion: String(dx?.justificacion || ''),
      datosRelevantes: String(dx?.datosRelevantes || ''),
      objetivo: objetivoConFormula(dx, { plazoElegido: opciones.plazoElegido }),
      ...(enunciadoDe(dx) ? { enunciado: enunciadoDe(dx) } : {}),
      noc: (dx?.noc || []).map((n) => ({ code: n.code || '', label: String(n.label || ''), indicadores: (n.indicadores || []).map((i) => ({ texto: String(i?.texto || ''), plazo: i?.plazo || null, evaluar: i?.evaluar || null })) })),
      actividades: actividadesConPlan(dx, { fecha: opciones.fechaEntrega, turno: opciones.turno, responsable: opciones.responsable }),
      evaluacion: { opciones: EVALUACION_OPCIONES, marcada: null },
    });
  });

  const camposDe = (sid) => [...pauta.camposSeccion, ...((pauta.camposExtraPorSeccion || {})[sid] || [])];
  const listaSecciones = [...secciones, SECCION_OTROS]
    .map((s) => {
      const marcada = marcadaEnValoracion(opciones.valoracion, pautaId, s.id);
      return {
        id: s.id,
        nombre: s.nombre,
        diagnosticos: porSeccion.get(s.id) || [],
        prosa: Object.fromEntries(camposDe(s.id).map((c) => [c, null])),
        ...(marcada === undefined ? {} : { marcada }),
      };
    })
    // Una sección sin diagnósticos se imprime igual (la rúbrica pide TODOS los
    // patrones, y lo que no se planificó también se documenta, con la frase
    // de `textoSeccionSinDiagnostico`); "Otros hallazgos" solo si recibió algo.
    .filter((s) => s.diagnosticos.length || s.id !== SECCION_OTROS.id);

  return {
    pautaId,
    pautaNombre: pauta.nombre,
    fuente: pauta.fuente,
    // El sanitizador del PDF escribe un guion largo cuando falta el dato; acá
    // vacío es vacío, y el guion largo no puede entrar al documento.
    paciente: { iniciales: sinPlaceholder(pae?.paciente?.iniciales), edad: sinPlaceholder(pae?.paciente?.edad), servicio: sinPlaceholder(pae?.paciente?.servicio) },
    dxMedico: sinPlaceholder(pae?.dxMedico),
    fechaEntrega: opciones.fechaEntrega || null,
    jerarquiza: !!pauta.jerarquiza,
    secciones: listaSecciones,
    totalDiagnosticos: diagnosticos.length,
  };
}

// Aplica la prosa de la IA al esqueleto: SOLO los campos permitidos de cada
// sección, SOLO strings. Todo lo demás se ignora (la red ya lo rechazó antes,
// esto es el segundo candado). Devuelve un esqueleto nuevo.
export function integrarProsaPauta(esqueleto, prosa) {
  if (!esqueleto) return esqueleto;
  const porSeccion = (prosa && prosa.secciones) || {};
  return {
    ...esqueleto,
    secciones: esqueleto.secciones.map((s) => {
      const entrada = porSeccion[s.id] || {};
      const nueva = { ...s.prosa };
      for (const campo of Object.keys(s.prosa)) {
        if (typeof entrada[campo] === 'string' && entrada[campo].trim()) nueva[campo] = entrada[campo].trim();
      }
      return { ...s, prosa: nueva };
    }),
  };
}

// ─── La red: verificarPauta ──────────────────────────────────────────────────
//
// Misma disciplina que verificarPulido (evolucionIA.js): fail-safe hacia NO
// aceptar, y el motivo se nombra (nada muere mudo). Vigila la salida de la IA
// contra el esqueleto:
//  (a) solo secciones que existen en el esqueleto y solo campos permitidos;
//  (b) ningún código NANDA (5 dígitos) que no esté en el PAE;
//  (c) ninguna cifra con unidad que el PAE no traiga (anti-invención);
//  (d) sin PII dura;
//  (e) sin guiones largos ni emojis, largo acotado.
// Devuelve { ok, motivos[], prosaLimpia } donde prosaLimpia conserva solo los
// campos que pasaron TODAS las reglas: una sección mala no bota a las demás
// (a diferencia del pulido, aquí cada campo es independiente y el fail-safe
// es "por completar" en ese campo, no perder el documento).
export const MAX_LARGO_CAMPO = 700;
const RE_CODIGO_NANDA = /\b\d{5}\b/g;
const RE_CIFRA = /\d+(?:[.,]\d+)?\s?(?:%|mmhg|lpm|rpm|mg|ml|cc|g|kg|°c|ºc|cmh2o|meq|mmol|ui|h|hrs?|d[ií]as?|min)(?![a-z0-9])/gi;
const RE_GUION_LARGO = /[—–]/;
const RE_EMOJI = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u;

function cifrasDe(texto) {
  return new Set((String(texto || '').toLowerCase().replace(/₂/g, '2').match(RE_CIFRA) || []).map((m) => m.replace(/\s+/g, '')));
}

function textoDelEsqueleto(esqueleto) {
  const partes = [esqueleto.dxMedico];
  for (const s of esqueleto.secciones) for (const d of s.diagnosticos) {
    partes.push(d.titulo, d.justificacion, d.datosRelevantes, d.objetivo);
    for (const n of d.noc) { partes.push(n.label); for (const i of n.indicadores) partes.push(i.texto, i.plazo); }
    for (const a of d.actividades) partes.push(a.texto);
  }
  return partes.filter(Boolean).join('\n');
}

export function verificarPauta(esqueleto, prosa) {
  const motivos = [];
  const limpia = { secciones: {} };
  if (!esqueleto || !prosa || typeof prosa !== 'object' || typeof prosa.secciones !== 'object' || !prosa.secciones) {
    return { ok: false, motivos: ['formato_invalido'], prosaLimpia: limpia };
  }
  const codigosPae = new Set(esqueleto.secciones.flatMap((s) => s.diagnosticos.map((d) => d.codigo)).filter(Boolean));
  const cifrasPae = cifrasDe(textoDelEsqueleto(esqueleto));
  const seccionesPorId = new Map(esqueleto.secciones.map((s) => [s.id, s]));

  for (const [sid, campos] of Object.entries(prosa.secciones)) {
    const sec = seccionesPorId.get(sid);
    if (!sec) { motivos.push(`seccion_desconocida:${sid}`); continue; }
    if (!campos || typeof campos !== 'object') { motivos.push(`formato_invalido:${sid}`); continue; }
    for (const [campo, valor] of Object.entries(campos)) {
      if (!(campo in sec.prosa)) { motivos.push(`campo_no_permitido:${sid}.${campo}`); continue; }
      if (typeof valor !== 'string') { motivos.push(`formato_invalido:${sid}.${campo}`); continue; }
      const texto = valor.trim();
      if (!texto) continue;
      if (texto.length > MAX_LARGO_CAMPO) { motivos.push(`largo:${sid}.${campo}`); continue; }
      if (RE_GUION_LARGO.test(texto) || RE_EMOJI.test(texto)) { motivos.push(`estilo:${sid}.${campo}`); continue; }
      const codigosNuevos = (texto.match(RE_CODIGO_NANDA) || []).filter((c) => !codigosPae.has(c));
      if (codigosNuevos.length) { motivos.push(`codigo_inventado:${sid}.${campo}`); continue; }
      const cifrasNuevas = [...cifrasDe(texto)].filter((c) => !cifrasPae.has(c));
      if (cifrasNuevas.length) { motivos.push(`cifra_inventada:${sid}.${campo}`); continue; }
      if (detectPiiDuro(texto).hit) { motivos.push(`pii:${sid}.${campo}`); continue; }
      limpia.secciones[sid] = { ...(limpia.secciones[sid] || {}), [campo]: texto };
    }
  }
  return { ok: motivos.length === 0, motivos, prosaLimpia: limpia };
}

// ─── El documento ────────────────────────────────────────────────────────────
//
// Texto plano, el MISMO para preview, Copiar y PDF (regla del repo: preview y
// documento salen de una sola función). Sin guiones largos, sin hora en la
// prosa generada salvo la hora de cada actividad, que ES el dato que la pauta
// pide. Una sección sin diagnósticos dice lo que corresponde según la
// valoración (`textoSeccionSinDiagnostico`): la rúbrica exige documentar todos
// los patrones, y "sin alteración" solo es un dato si no se marcó alterada.
export const POR_COMPLETAR = 'Por completar por el estudiante.';

function fechaLarga(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  return `${d} de ${meses[m - 1]} de ${y}`;
}

export function documentoPauta(esqueleto) {
  if (!esqueleto) return '';
  const L = [];
  L.push(`Proceso de Atención de Enfermería, pauta: ${esqueleto.pautaNombre}`);
  const cab = [];
  if (esqueleto.paciente.iniciales) cab.push(`Paciente: ${esqueleto.paciente.iniciales}`);
  if (esqueleto.paciente.edad) cab.push(`${esqueleto.paciente.edad} años`);
  if (esqueleto.paciente.servicio) cab.push(esqueleto.paciente.servicio);
  if (cab.length) L.push(cab.join(', '));
  if (esqueleto.dxMedico) L.push(`Diagnóstico médico: ${esqueleto.dxMedico}`);
  if (esqueleto.fechaEntrega) L.push(`Fecha de entrega: ${fechaLarga(esqueleto.fechaEntrega)}`);
  L.push('');

  if (esqueleto.jerarquiza) {
    const todos = esqueleto.secciones.flatMap((s) => s.diagnosticos).sort((a, b) => a.orden - b.orden);
    if (todos.length) {
      L.push('Jerarquización de diagnósticos');
      todos.forEach((d, i) => L.push(`${i + 1}. ${d.titulo}${d.codigo ? ` (${d.codigo})` : ''}${d.justificacion ? `. Justificación: ${d.justificacion}` : ''}`));
      L.push('');
    }
  }

  esqueleto.secciones.forEach((s, idx) => {
    L.push(`${idx + 1}. ${s.nombre}`);
    for (const [campo, valor] of Object.entries(s.prosa)) {
      L.push(`${CAMPO_LABEL[campo] || campo}: ${valor || POR_COMPLETAR}`);
    }
    if (!s.diagnosticos.length) {
      L.push(textoSeccionSinDiagnostico(s));
      L.push('');
      return;
    }
    for (const d of s.diagnosticos) {
      L.push(`Diagnóstico de enfermería: ${d.titulo}${d.codigo ? ` (${d.codigo})` : ''}${d.prioridad ? `, ${d.prioridad.toLowerCase()}` : ''}`);
      // El enunciado tal como pasó la revisión, con los mismos rótulos de la
      // pantalla (DxClause). Un PAE sin enunciado imprime como antes.
      if (d.enunciado?.relacionadoCon) L.push(`Relacionado con: ${d.enunciado.relacionadoCon}`);
      if (d.enunciado?.manifestadoPor) L.push(`Manifestado por: ${d.enunciado.manifestadoPor}`);
      if (d.enunciado?.razonamiento) L.push(`Razonamiento clínico: ${d.enunciado.razonamiento}`);
      if (d.datosRelevantes) L.push(`Datos relevantes: ${d.datosRelevantes}`);
      L.push(`Objetivo: ${d.objetivo}`);
      if (d.actividades.length) {
        L.push('Actividades:');
        d.actividades.forEach((a, i) => {
          const cuando = [a.fecha ? fechaLarga(a.fecha) : null, a.hora].filter(Boolean).join(', ');
          L.push(`${i + 1}. ${a.texto} (${cuando ? `${cuando}; ` : ''}responsable: ${a.responsable})`);
        });
      }
      L.push(`Evaluación: ${d.evaluacion.marcada || d.evaluacion.opciones.join(' / ')}`);
      L.push('');
    }
  });

  L.push('Documento educativo generado con MiPAE a partir de tu PAE. No reemplaza tu criterio ni el de tu docente.');
  return L.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

// Nombre del estudiante y su universidad en el PDF de la pauta (opcional,
// decisión del fundador 2026-10-06: "que se pueda agregar"). Son de quien
// entrega el PAE, no del paciente: viajan SOLO al PDF, jamás al esqueleto ni a
// la IA de la pauta, ni a telemetría ni a ningún almacenamiento. Se recortan a
// una línea y a 120 caracteres; vacío = no se imprime.
export function autorPauta(autor) {
  const limpio = (v) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, 120) : '');
  const estudiante = limpio(autor?.estudiante);
  const universidad = limpio(autor?.universidad);
  return estudiante || universidad ? { estudiante, universidad } : null;
}

// Payload Handlebars para api/pdf/generate (plantilla pae-pauta.html). Sale
// del MISMO esqueleto que el documento de texto: no hay una segunda fuente.
export function buildPautaPayload(esqueleto, { paeId, autor = null } = {}) {
  if (!esqueleto) return null;
  const todos = esqueleto.secciones.flatMap((s) => s.diagnosticos).sort((a, b) => a.orden - b.orden);
  const deQuien = autorPauta(autor);
  return {
    ...(deQuien ? { estudiante: deQuien.estudiante, universidad: deQuien.universidad } : {}),
    titulo: `PAE en pauta: ${esqueleto.pautaNombre}`,
    pautaNombre: esqueleto.pautaNombre,
    fuente: esqueleto.fuente || '',
    paciente: { ...esqueleto.paciente },
    dxMedico: esqueleto.dxMedico,
    fechaEntrega: fechaLarga(esqueleto.fechaEntrega) || '',
    fechaLarga: fechaLarga(new Date().toISOString().slice(0, 10)),
    jerarquizacion: esqueleto.jerarquiza ? todos.map((d) => ({ titulo: d.titulo, codigo: d.codigo, justificacion: d.justificacion })) : [],
    secciones: esqueleto.secciones.map((s, i) => ({
      numero: i + 1,
      nombre: s.nombre,
      sinDiagnostico: textoSeccionSinDiagnostico(s),
      prosa: Object.entries(s.prosa).map(([campo, valor]) => ({ label: CAMPO_LABEL[campo] || campo, valor: valor || POR_COMPLETAR })),
      diagnosticos: s.diagnosticos.map((d) => ({
        codigo: d.codigo,
        titulo: d.titulo,
        prioridad: d.prioridad,
        relacionadoCon: d.enunciado?.relacionadoCon || '',
        manifestadoPor: d.enunciado?.manifestadoPor || '',
        razonamiento: d.enunciado?.razonamiento || '',
        datosRelevantes: d.datosRelevantes,
        objetivo: d.objetivo,
        actividades: d.actividades.map((a) => ({ texto: a.texto, nicCode: a.nicCode, nicLabel: a.nicLabel, fecha: fechaLarga(a.fecha) || '', hora: a.hora, responsable: a.responsable })),
        evaluacion: d.evaluacion.marcada || d.evaluacion.opciones.join(' / '),
      })),
    })),
    trazabilidad: { paeId: paeId || '' },
  };
}

// Para la UI "de dónde salió": por sección, los diagnósticos con su origen.
export function trazaPauta(esqueleto) {
  if (!esqueleto) return [];
  return esqueleto.secciones.map((s) => ({
    id: s.id,
    nombre: s.nombre,
    origen: s.diagnosticos.map((d) => ({ codigo: d.codigo, titulo: d.titulo, noc: d.noc.map((n) => n.label), nic: [...new Set(d.actividades.map((a) => a.nicLabel).filter(Boolean))] })),
  }));
}
